import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  Inventory,
  PlaylistAddCheck,
  Save,
} from "@mui/icons-material";
import { purchaseAPI, racksAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from '../../../../INV/pharmecy/contexts/AuthContext';

export const InvReceivePurchase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const isPartial = searchParams.get("partial") === "true";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [batches, setBatches] = useState([]);
  const [racks, setRacks] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const steps = ["Review Order", "Enter Batch Details", "Confirmation"];

  useEffect(() => {
    fetchPurchaseOrder();
    fetchRacks();
  }, [id]);

  const fetchPurchaseOrder = async () => {
    try {
      const response = await purchaseAPI.getById(id);
      const order = response.data.data.purchaseOrder;
      setPurchaseOrder(order);

      // Initialize batches for each item
      const initialBatches = order.items.map((item, index) => ({
        itemIndex: index,
        batchNumber: generateBatchNumber(item.item),
        quantity: isPartial ? 0 : Number(item.quantity),
        expiryDate: "",
        manufactureDate: new Date().toISOString().split("T")[0],
        rack: "",
        receivedQuantity: isPartial ? 0 : Number(item.quantity),
      }));

      setBatches(initialBatches);
    } catch (error) {
      enqueueSnackbar("Error fetching purchase order", { variant: "error" });
      navigate("/hospital/hospital-inventory/purchases");
    } finally {
      setLoading(false);
    }
  };

  const fetchRacks = async () => {
    try {
      const response = await racksAPI.getAvailable();
      if (response.data.success) {
        setRacks(response.data.data.racks || []);
      } else {
        enqueueSnackbar("Error fetching racks", { variant: "error" });
        setRacks([]);
      }
    } catch (error) {
      console.error("Error fetching racks:", error);
      enqueueSnackbar("Error fetching rack locations", { variant: "error" });
      setRacks([]);
    }
  };

  const generateBatchNumber = (item) => {
    const prefix = item.name.substring(0, 3).toUpperCase();
    const timestamp = Date.now().toString().slice(-6);
    return `${prefix}-${timestamp}`;
  };

  const handleBatchChange = (index, field, value) => {
    const updatedBatches = [...batches];
    updatedBatches[index] = {
      ...updatedBatches[index],
      [field]: value,
    };

    // If it's partial receiving and quantity changes, update receivedQuantity
    if (isPartial && field === "quantity") {
      updatedBatches[index].receivedQuantity = parseInt(value) || 0;
    }

    if (field === "quantity") {
      updatedBatches[index][field] = Number(value) || 0;
    }

    setBatches(updatedBatches);
  };

  const validateBatches = () => {
    for (const batch of batches) {
      if (!batch.batchNumber.trim()) {
        enqueueSnackbar("Batch number is required for all items", {
          variant: "warning",
        });
        return false;
      }
      if (!batch.expiryDate) {
        enqueueSnackbar("Expiry date is required for all items", {
          variant: "warning",
        });
        return false;
      }
      if (!batch.manufactureDate) {
        enqueueSnackbar("Manufacture date is required for all items", {
          variant: "warning",
        });
        return false;
      }
      if (
        isPartial &&
        (batch.quantity <= 0 ||
          batch.quantity > purchaseOrder.items[batch.itemIndex].quantity)
      ) {
        enqueueSnackbar(
          `Invalid quantity for ${
            purchaseOrder.items[batch.itemIndex].item.name
          }`,
          { variant: "warning" }
        );
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 1 && !validateBatches()) {
      return;
    }
    if (activeStep === 2) {
      setConfirmationOpen(true);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleReceive = async () => {
    if (!validateBatches()) {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        batches: batches
          .filter((batch) => {
            const quantity = isPartial
              ? batch.quantity
              : purchaseOrder.items[batch.itemIndex].quantity;
            return quantity > 0;
          })
          .map((batch) => ({
            itemIndex: batch.itemIndex,
            batchNumber: batch.batchNumber,
            expiryDate: batch.expiryDate,
            manufactureDate: batch.manufactureDate,
            rack: batch.rack || undefined,
            quantity: isPartial ? Number(batch.quantity) : undefined,
          })),
      };

      await purchaseAPI.receive(id, payload);

      enqueueSnackbar(
        `Purchase order ${
          isPartial ? "partially received" : "received"
        } successfully`,
        { variant: "success" }
      );

      setConfirmationOpen(false);
      navigate("/hospital/hospital-inventory/purchases");
    } catch (error) {
      console.error("Receive error:", error);
      const errorMessage =
        error.response?.data?.message || "Error receiving purchase order";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const calculateReceivedTotal = () => {
    return batches.reduce((total, batch) => {
      const item = purchaseOrder?.items[batch.itemIndex];
      if (item) {
        const quantity = isPartial ? batch.quantity : item.quantity;
        return total + quantity * item.costPrice;
      }
      return total;
    }, 0);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!purchaseOrder) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Typography>Purchase order not found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate("/hospital/hospital-inventory/purchases")}
          >
            Back
          </Button>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {isPartial ? "Partially Receive" : "Receive"} Purchase Order
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {purchaseOrder.poNumber} - {purchaseOrder.supplier.name}
            </Typography>
          </Box>
        </Box>

        <Chip
          icon={isPartial ? <PlaylistAddCheck /> : <CheckCircle />}
          label={isPartial ? "Partial Receiving" : "Complete Receiving"}
          color={isPartial ? "warning" : "success"}
          variant="outlined"
        />
      </Box>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step 1: Review Order */}
      {activeStep === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Order Items
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="center">Ordered Qty</TableCell>
                        <TableCell align="center">Cost Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {purchaseOrder.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {item.item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.item.genericName} • {item.item.strength}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1">
                              {item.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1">
                              ₹{item.costPrice}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold">
                              ₹{(item.quantity * item.costPrice).toFixed(2)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ position: "sticky", top: 100 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Order Summary
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Supplier
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {purchaseOrder.supplier.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {purchaseOrder.supplier.contactPerson} •{" "}
                    {purchaseOrder.supplier.phone}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(purchaseOrder.orderDate).toLocaleDateString()}
                  </Typography>
                </Box>

                {purchaseOrder.expectedDeliveryDate && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Expected Delivery
                    </Typography>
                    <Typography variant="body1">
                      {new Date(
                        purchaseOrder.expectedDeliveryDate
                      ).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">
                      ₹{purchaseOrder.totalAmount?.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Tax:</Typography>
                    <Typography variant="body2">
                      ₹{purchaseOrder.taxAmount?.toFixed(2)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Total:
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      ₹{purchaseOrder.grandTotal?.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>

                <Alert severity={isPartial ? "warning" : "info"} sx={{ mb: 2 }}>
                  {isPartial
                    ? "You are about to partially receive this order. Enter quantities for items you are receiving."
                    : "You are about to mark this entire order as received."}
                </Alert>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleNext}
                  startIcon={<Inventory />}
                >
                  Continue to Batch Details
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 2: Batch Details */}
      {activeStep === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Enter Batch Details
                </Typography>

                {isPartial && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Enter the quantities you are receiving for each item. You
                    can receive less than the ordered quantity.
                  </Alert>
                )}

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="center">Ordered Qty</TableCell>
                        {isPartial && (
                          <TableCell align="center">Receiving Qty</TableCell>
                        )}
                        <TableCell>Batch Number</TableCell>
                        <TableCell>Manufacture Date</TableCell>
                        <TableCell>Expiry Date</TableCell>
                        <TableCell>Rack/Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batches.map((batch, index) => {
                        const item = purchaseOrder.items[batch.itemIndex];
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {item.item.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.item.genericName}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1">
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            {isPartial && (
                              <TableCell align="center">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={batch.quantity}
                                  onChange={(e) =>
                                    handleBatchChange(
                                      index,
                                      "quantity",
                                      e.target.value
                                    )
                                  }
                                  inputProps={{
                                    min: 0,
                                    max: item.quantity,
                                    style: { textAlign: "center" },
                                  }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <TextField
                                size="small"
                                value={batch.batchNumber}
                                onChange={(e) =>
                                  handleBatchChange(
                                    index,
                                    "batchNumber",
                                    e.target.value
                                  )
                                }
                                placeholder="Batch number"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="date"
                                size="small"
                                value={batch.manufactureDate}
                                onChange={(e) =>
                                  handleBatchChange(
                                    index,
                                    "manufactureDate",
                                    e.target.value
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="date"
                                size="small"
                                value={batch.expiryDate}
                                onChange={(e) =>
                                  handleBatchChange(
                                    index,
                                    "expiryDate",
                                    e.target.value
                                  )
                                }
                                InputLabelProps={{ shrink: true }}
                                required
                              />
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Select Rack</InputLabel>
                                <Select
                                  value={batch.rack || ""}
                                  label="Select Rack"
                                  onChange={(e) =>
                                    handleBatchChange(
                                      index,
                                      "rack",
                                      e.target.value
                                    )
                                  }
                                >
                                  <MenuItem value="">
                                    <em>None</em>
                                  </MenuItem>
                                  {racks.map((rack) => (
                                    <MenuItem key={rack._id} value={rack._id}>
                                      {rack.name}{" "}
                                      {rack.location
                                        ? `(${rack.location.aisle}-${rack.location.row}-${rack.location.section})`
                                        : ""}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {isPartial && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      bgcolor: "background.default",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom>
                      Receiving Summary
                    </Typography>
                    <Typography variant="body2">
                      Total Value: ₹{calculateReceivedTotal().toFixed(2)}
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 3,
                  }}
                >
                  <Button onClick={handleBack} variant="outlined">
                    Back
                  </Button>
                  <Button onClick={handleNext} variant="contained">
                    Review & Confirm
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 3: Confirmation */}
      {activeStep === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Confirmation
                </Typography>

                <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircle />}>
                  Ready to {isPartial ? "partially receive" : "receive"} this
                  order. Please review the details below.
                </Alert>

                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ mb: 3 }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                        <TableCell>Batch Number</TableCell>
                        <TableCell>Expiry Date</TableCell>
                        <TableCell>Rack Location</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {batches.map((batch, index) => {
                        const item = purchaseOrder.items[batch.itemIndex];
                        const quantity = isPartial
                          ? batch.quantity
                          : item.quantity;
                        const selectedRack = racks.find(
                          (rack) => rack._id === batch.rack
                        );

                        if (isPartial && quantity === 0) return null;

                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {item.item.name}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1">
                                {quantity}
                                {isPartial && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    of {item.quantity} ordered
                                  </Typography>
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1">
                                {batch.batchNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1">
                                {new Date(
                                  batch.expiryDate
                                ).toLocaleDateString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body1">
                                {selectedRack
                                  ? `${selectedRack.name} ${
                                      selectedRack.location
                                        ? `(${selectedRack.location})`
                                        : ""
                                    }`
                                  : "Not assigned"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 3,
                  }}
                >
                  <Button onClick={handleBack} variant="outlined">
                    Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    variant="contained"
                    startIcon={<CheckCircle />}
                  >
                    {isPartial
                      ? "Partially Receive Order"
                      : "Receive Complete Order"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm {isPartial ? "Partial" : "Complete"} Receiving
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will update inventory stock and cannot be undone.
          </Alert>
          <Typography variant="body1">
            Are you sure you want to{" "}
            {isPartial ? "partially receive" : "mark as received"} purchase
            order <strong>{purchaseOrder.poNumber}</strong>?
          </Typography>
          {isPartial && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Total value: ₹{calculateReceivedTotal().toFixed(2)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmationOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReceive}
            variant="contained"
            disabled={saving}
            startIcon={saving ? null : <Save />}
          >
            {saving ? "Processing..." : "Confirm Receiving"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};